import * as XLSX from "xlsx-js-style";
import { formatForNetDates } from "./datetime";

export function downloadCsv(data, filename) {
  const encoding = "data:text/csv;charset=utf-8,%EF%BB%BF";
  const link = document.createElement("a");
  link.setAttribute("href", encoding + escape(data));
  link.setAttribute("download", `${filename}.csv`);

  document.body.appendChild(link); // Required for FF

  link.click();
}

const normalizeBase64 = (base64Value) => {
  const unquoted =
    base64Value.startsWith('"') && base64Value.endsWith('"')
      ? base64Value.slice(1, -1)
      : base64Value;
  const dataPart = unquoted.includes(",")
    ? unquoted.split(",")[1]
    : unquoted;

  return dataPart.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
}

export const base64ToUint8Array = (base64Value) => {
  const cleanBase64 = normalizeBase64(base64Value);
  const binaryString = atob(cleanBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
};

export function downloadFile(file, name) {
  const url = window.URL.createObjectURL(new Blob([file]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", name);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
}

export const exportToExcel = (columns, dataExport, title, fileName) => {
  const headerRow = columns.map((column) => column.header);

  // Format rows data and keep track of cell configurations
  const formattedDataRows = dataExport.map((row) =>
    columns.map((col) => {
      const value = row[col.accessorKey] ?? 0;
      let cellValue = typeof value === "number" ? value : String(value);
      if (cellValue === 0 || cellValue === "0") {
        cellValue = "";
      }
      if (
        typeof cellValue === "string" &&
        cellValue.includes(":") &&
        col.accessorKey.toLowerCase().includes("fecha")
      ) {
        cellValue = formatForNetDates(cellValue, col.xlsxConfig?.dateFormat);
      }
      return {
        value: cellValue,
        isNumber: typeof value === "number",
        numberFormat: col.xlsxConfig?.numberFormat,
      };
    })
  );

  const rows = formattedDataRows.map(row => row.map(cell => cell.value));
  const worksheetData = [[title], [""], headerRow, ...rows];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Apply properties (like row heights)
  worksheet["!rows"] = [{ hpt: 22 }]; // Optional default or first row. SheetJS standardly uses this array.

  // Title styling
  if (worksheet["A1"]) {
    worksheet["A1"].s = {
      font: { name: "Arial", sz: 20, bold: true, color: { rgb: "433F73" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: { bottom: { style: "medium", color: { rgb: "433F73" } } }
    };
  }

  // Header styling (Row 3, 0-indexed r: 2)
  columns.forEach((_, colIndex) => {
    const cellRef = XLSX.utils.encode_cell({ r: 2, c: colIndex });
    if (!worksheet[cellRef]) worksheet[cellRef] = { t: "s", v: "" };
    worksheet[cellRef].s = {
      font: { name: "Arial", sz: 10, bold: true, color: { rgb: "433F73" } },
      fill: { fgColor: { rgb: "FFFFFF" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        left: colIndex === 0 ? { style: "thin" } : undefined,
        right: colIndex === columns.length - 1 ? { style: "thin" } : undefined,
        bottom: { style: "thin" },
        top: { style: "thin" }
      }
    };
  });

  // Apply row styles and number formatting
  formattedDataRows.forEach((rowValues, rIndex) => {
    const rowRef = rIndex + 3; // r: 3 for 4th row
    rowValues.forEach((cellConfig, cIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowRef, c: cIndex });
      if (!worksheet[cellRef]) return; // Handled by aoa_to_sheet if value exists

      const style = {
        fill: { fgColor: { rgb: rIndex % 2 === 0 ? "DDDDDD" : "FFFFFF" } },
        border: {
          left: cIndex === 0 ? { style: "thin" } : undefined,
          right: cIndex === columns.length - 1 ? { style: "thin" } : undefined,
          bottom: rIndex === formattedDataRows.length - 1 ? { style: "thin" } : undefined
        },
        alignment: { horizontal: "center", vertical: "center" }
      };

      let numFmt = undefined;
      if (cellConfig.numberFormat) {
        numFmt = cellConfig.numberFormat;
      } else if (cellConfig.isNumber && typeof cellConfig.value === "number" && cellConfig.value % 1 !== 0) {
        numFmt = "#,##0.##";
      } else if (cellConfig.isNumber) {
        numFmt = "#,##0";
      }

      worksheet[cellRef].s = style;
      if (numFmt) {
        worksheet[cellRef].z = numFmt;
      }
    });
  });

  if (columns.length > 0) {
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
    ];
    // Apply bottom border to all cells in the title merged row to ensure rendering
    for (let c = 1; c < columns.length; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c });
      if (!worksheet[cellRef]) worksheet[cellRef] = { t: "z" };
      worksheet[cellRef].s = { border: { bottom: { style: "medium", color: { rgb: "433F73" } } } };
    }
  }

  worksheet["!cols"] = columns.map((column) => {
    const header = String(column.header || "").trim();
    if (header === "Siniestro") return { wch: 15 };
    if (header === "Nombre") return { wch: 40 };
    if (header === "Prestador Inicial") return { wch: 60 };
    if (header === "Establecimiento" || header === "Tiene Programa de Seguridad") return { wch: 35 };
    if (header === "Establecimiento / Lugar" || header === "Domicilio") return { wch: 80 };
    if (header === "Establecimiento SRT (Cod Int)" || header === "Establecimiento Empresa (MU)") return { wch: 30 };
    if (header === "Intereses por Mora Devengados") return { wch: 35 };
    return { wch: 25 };
  });

  const workbook = XLSX.utils.book_new();
  const sheetName = (title || "Reporte").slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadFile(blob, `${fileName ?? title}.xlsx`);
};

/**
 * @param {string[]} encabezado - El encabezado de la tabla
 * @param {object[]} data - Las filas de la tabla
 * @param {string} title - El titulo del archivo exportado
 */
export const exportToCSV = (encabezado, data, title, fileName) => {
  const csvContent = [
    encabezado.map((column) => column.header),
    ...data.map((row) =>
      encabezado.map((col) => row[col.accessorKey] || "").map(String)
    ),
  ]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });

  downloadFile(blob, `${fileName ?? title}.csv`);
};


export async function parseBlobToObject(blob) {
  const text = await blob.text();
  try {
    return JSON.parse(text);
  } catch {

    return null
  }
}

export function mapDocumentFilesToMetadata(files) {
  const fileMetadata = [];
  files.forEach((f) => {
    fileMetadata.push({
      base64: f.value,
      extension: f.filename.split(".").pop(),
      nombre: f.filename.split(".")[0],
    });
  });
  return fileMetadata;
}
